import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import PageHero from '@/components/feature/PageHero';
import SectionHeading from '@/components/base/SectionHeading';
import CTABanner from '@/components/feature/CTABanner';
import FAQAccordion from '@/components/base/FAQAccordion';
import { useSEO } from '@/hooks/useSEO';

const servicos = [
  { icon: 'ri-sofa-line', title: 'Móveis Planejados para Escritório', description: 'Estações de trabalho, armários embutidos, bancadas e painéis sob medida para aproveitar cada centímetro do seu espaço corporativo.' },
  { icon: 'ri-door-open-line', title: 'Recepções e Balcões', description: 'Balcões de recepção, painéis de identidade visual e mobiliário de entrada que causam a primeira impressão certa nos seus clientes.' },
  { icon: 'ri-layout-grid-line', title: 'Salas de Reunião', description: 'Mesas de reunião, aparadores, painéis e armários planejados para salas executivas com acabamento premium e funcionalidade total.' },
  { icon: 'ri-archive-line', title: 'Armários e Roupeiros', description: 'Armários embutidos, roupeiros corporativos e soluções de armazenamento personalizadas para vestiários, refeitórios e áreas de apoio.' },
  { icon: 'ri-home-office-line', title: 'Home Office Personalizado', description: 'Projetos completos de home office com mesa, estante, painel e armário integrados, aproveitando o espaço disponível com design moderno.' },
  { icon: 'ri-restaurant-line', title: 'Cozinhas e Refeitórios', description: 'Cozinhas corporativas, refeitórios e áreas de copa planejadas com materiais resistentes e acabamento profissional.' },
];

const diferenciais = [
  { icon: 'ri-ruler-2-line', title: 'Sob Medida', description: 'Cada projeto é único. Desenvolvemos o mobiliário exatamente nas dimensões e configurações do seu espaço.' },
  { icon: 'ri-palette-line', title: 'Acabamentos Premium', description: 'MDF, MDP, BP, laminados, vidro, alumínio e madeira maciça. Diversas opções de cor e textura.' },
  { icon: 'ri-tools-line', title: 'Instalação Completa', description: 'Nossa equipe cuida de tudo: projeto, fabricação, entrega e instalação no local.' },
  { icon: 'ri-draft-line', title: 'Projeto 3D', description: 'Visualize o resultado antes de fabricar com renderização 3D do seu ambiente.' },
];

const portfolioItems = [
  { title: 'Recepção Corporativa', description: 'Balcão de recepção em MDF com painel de identidade visual e iluminação embutida.', image: 'https://readdy.ai/api/search-image?query=modern%20corporate%20reception%20desk%20custom%20woodwork%20MDF%20white%20lacquer%20LED%20lighting%20brand%20panel%20professional%20office%20interior%20elegant%20minimalist%20design%20clean%20background&width=800&height=600&seq=marc-01&orientation=landscape' },
  { title: 'Sala de Reunião', description: 'Mesa de reunião sob medida com aparador e painel de TV integrado.', image: 'https://readdy.ai/api/search-image?query=custom%20conference%20room%20furniture%20meeting%20table%20built-in%20TV%20panel%20sideboard%20wood%20finish%20corporate%20office%20interior%20elegant%20professional%20design%20clean%20background&width=800&height=600&seq=marc-02&orientation=landscape' },
  { title: 'Estações de Trabalho', description: 'Bancadas planejadas com divisórias e armários superiores para open office.', image: 'https://readdy.ai/api/search-image?query=custom%20built-in%20workstation%20office%20furniture%20planned%20desk%20with%20overhead%20cabinets%20dividers%20open%20office%20corporate%20interior%20modern%20minimalist%20design%20clean%20background&width=800&height=600&seq=marc-03&orientation=landscape' },
  { title: 'Home Office', description: 'Projeto completo de home office com mesa, estante e painel integrados.', image: 'https://readdy.ai/api/search-image?query=custom%20home%20office%20furniture%20planned%20desk%20bookshelf%20integrated%20panel%20wood%20finish%20modern%20minimalist%20design%20clean%20background%20professional%20workspace%20elegant&width=800&height=600&seq=marc-04&orientation=landscape' },
  { title: 'Armários Embutidos', description: 'Armários planejados do piso ao teto para vestiários e áreas de apoio.', image: 'https://readdy.ai/api/search-image?query=custom%20built-in%20floor%20to%20ceiling%20cabinets%20corporate%20office%20storage%20wardrobe%20wood%20finish%20modern%20minimalist%20design%20clean%20background%20professional%20elegant&width=800&height=600&seq=marc-05&orientation=landscape' },
  { title: 'Cozinha Corporativa', description: 'Copa e cozinha planejada com bancada, armários e acabamento profissional.', image: 'https://readdy.ai/api/search-image?query=custom%20corporate%20kitchen%20pantry%20planned%20cabinets%20countertop%20professional%20office%20break%20room%20modern%20minimalist%20design%20clean%20background%20elegant%20wood%20finish&width=800&height=600&seq=marc-06&orientation=landscape' },
];

const faqs = [
  { q: 'A Home Office Premium faz móveis planejados sob medida?', a: 'Sim. Desenvolvemos projetos completos de marcenaria corporativa sob medida, incluindo estações de trabalho, recepções, salas de reunião, home office, armários embutidos e cozinhas corporativas.' },
  { q: 'Vocês fazem projeto 3D antes de fabricar?', a: 'Sim. Oferecemos projeto 3D gratuito para que você visualize o resultado antes da fabricação, garantindo que o mobiliário atenda exatamente às suas expectativas.' },
  { q: 'Quais materiais são usados na marcenaria corporativa?', a: 'Trabalhamos com MDF, MDP, BP, laminados, vidro, alumínio e madeira maciça, com diversas opções de cor e textura para combinar com a identidade visual da sua empresa.' },
  { q: 'A instalação dos móveis planejados está inclusa?', a: 'Sim. Nossa equipe cuida de todo o processo: projeto, fabricação, entrega e instalação completa no local, com acabamento perfeito e limpeza do espaço ao final.' },
  { q: 'Atendem Caruaru e outras cidades de Pernambuco?', a: 'Sim. Atendemos Caruaru e toda a região de Pernambuco e Nordeste com serviços completos de marcenaria corporativa personalizada.' },
];

export default function MarcenariaPage() {
  useSEO({
    title: 'Marcenaria Personalizada Corporativa Caruaru PE | Home Office Premium',
    description: 'Marcenaria personalizada corporativa em Caruaru PE. Móveis planejados sob medida, recepções, salas de reunião, home office e cozinhas corporativas. Projeto 3D gratuito.',
    keywords: 'marcenaria corporativa Recife, móveis planejados escritório Petrolina, marcenaria sob medida Garanhuns, home office planejado Arcoverde, marcenaria corporativa Sertão PE, móveis planejados Pernambuco, marcenaria escritório Agreste, móveis sob medida Nordeste, marcenaria recepção Recife, marcenaria sala reunião Petrolina, marcenaria home office Garanhuns, marcenaria cozinha corporativa Caruaru',
    path: '/marcenaria',
    ogTitle: 'Marcenaria Personalizada Corporativa | Home Office Premium Caruaru',
    ogDescription: 'Móveis planejados sob medida para escritórios em Caruaru PE. Projeto 3D gratuito, fabricação e instalação completa.',
  });

  useEffect(() => {
    const schemaId = 'schema-marcenaria';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Marcenaria Personalizada Corporativa – Home Office Premium',
      description: 'Móveis planejados sob medida para escritórios em Caruaru PE. Recepções, salas de reunião, home office e cozinhas corporativas.',
      url: `${import.meta.env.VITE_SITE_URL ?? ''}/marcenaria`,
      provider: {
        '@type': 'LocalBusiness',
        name: 'Home Office Premium',
        address: { '@type': 'PostalAddress', addressLocality: 'Caruaru', addressRegion: 'PE', addressCountry: 'BR' },
      },
      areaServed: { '@type': 'State', name: 'Pernambuco' },
      serviceType: 'Marcenaria Corporativa Sob Medida',
    });

    const breadcrumbId = 'schema-marcenaria-breadcrumb';
    let bcScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = breadcrumbId; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL ?? '' },
        { '@type': 'ListItem', position: 2, name: 'Marcenaria', item: `${import.meta.env.VITE_SITE_URL ?? ''}/marcenaria` },
      ],
    });

    const faqSchemaId = 'schema-marcenaria-faq';
    let faqScript = document.getElementById(faqSchemaId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqSchemaId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    return () => { script?.remove(); bcScript?.remove(); faqScript?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <PageHero
        eyebrow="Projetos 100% sob medida"
        eyebrowIcon="ri-hammer-line"
        title={
          <>
            Marcenaria <span className="text-white/50 font-light">personalizada.</span>
          </>
        }
        subtitle="Transformamos seu espaço corporativo com móveis planejados sob medida. Do projeto à instalação, criamos ambientes únicos que refletem a identidade da sua empresa."
        image="https://readdy.ai/api/search-image?query=luxury%20custom%20woodwork%20corporate%20office%20interior%20bespoke%20furniture%20reception%20desk%20built-in%20cabinets%20warm%20wood%20tones%20elegant%20minimalist%20professional%20environment%20natural%20light%20sophisticated%20atmosphere&width=1600&height=1000&seq=marcenaria-hero-2026&orientation=landscape"
        alt="Marcenaria personalizada corporativa"
        breadcrumb={[
          { label: 'Home', to: '/' },
          { label: 'Marcenaria' },
        ]}
        ctas={[
          { label: 'Solicitar projeto', to: '/contato' },
          { label: 'Falar no WhatsApp', href: 'https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es', variant: 'secondary' },
        ]}
      />

      {/* Diferenciais */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {diferenciais.map((d) => (
              <div key={d.title}>
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-4">
                  <i className={`${d.icon} text-xl`}></i>
                </span>
                <h3 className="text-foreground-900 font-medium text-[16px] mb-2">{d.title}</h3>
                <p className="text-foreground-500 text-[14px] leading-relaxed font-light">{d.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading
            eyebrow="O que fazemos"
            title="Soluções completas em marcenaria."
            subtitle="Para todos os ambientes corporativos."
            className="mb-12"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicos.map((s) => (
              <div key={s.title} className="bg-white rounded-2xl p-8 border border-background-200/50 hover:border-primary-200/50 transition-colors duration-300">
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-background-50 text-foreground-500 mb-5">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <h3 className="text-foreground-900 font-medium text-[16px] mb-2">{s.title}</h3>
                <p className="text-foreground-500 text-[14px] leading-relaxed font-light">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfólio */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading
            eyebrow="Portfólio"
            title="Projetos realizados."
            subtitle="Cada projeto é único. Veja alguns exemplos do nosso trabalho."
            className="mb-12"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12">
            {portfolioItems.map((item) => (
              <div key={item.title} className="group">
                <div className="img-zoom rounded-xl overflow-hidden bg-background-100 aspect-[4/3]">
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="pt-4">
                  <p className="text-[11px] text-primary-600 font-medium uppercase tracking-wider">Marcenaria Personalizada</p>
                  <h3 className="text-foreground-900 font-medium text-[15px] mt-1">{item.title}</h3>
                  <p className="text-foreground-400 text-[13px] font-light mt-1 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading eyebrow="Processo" title="Como funciona." className="mb-12" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', icon: 'ri-chat-3-line', title: 'Consulta', description: 'Você nos conta o que precisa e visitamos o espaço para entender o projeto.' },
              { step: '02', icon: 'ri-draft-line', title: 'Projeto 3D', description: 'Desenvolvemos o projeto com renderização 3D para você visualizar o resultado.' },
              { step: '03', icon: 'ri-hammer-line', title: 'Fabricação', description: 'Fabricamos os móveis com materiais de alta qualidade no prazo combinado.' },
              { step: '04', icon: 'ri-check-double-line', title: 'Instalação', description: 'Nossa equipe instala tudo no local com acabamento perfeito e limpeza do espaço.' },
            ].map((item) => (
              <div key={item.step} className="border-t border-background-200/70 pt-6">
                <span className="text-primary-600 font-semibold text-[13px] tracking-wider">{item.step}</span>
                <h3 className="text-foreground-900 font-medium text-[16px] mt-2 mb-1">{item.title}</h3>
                <p className="text-foreground-500 text-[14px] leading-relaxed font-light">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading eyebrow="Perguntas frequentes" title="FAQ — Marcenaria." className="mb-12" />
          <div className="max-w-3xl">
            {faqs.map((f) => (
              <FAQAccordion key={f.q} question={f.q} answer={f.a} />
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Vamos criar algo único para sua empresa?"
        subtitle="Visita técnica gratuita, projeto 3D e orçamento sem compromisso."
        primaryLabel="Solicitar projeto"
        primaryTo="/contato"
        secondaryLabel="WhatsApp"
        secondaryHref="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
      />

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}